import { Component } from '@theme/component';

/**
 * @typedef {object} ProductAddOnsRefs
 * @property {HTMLDetailsElement} disclosure - The <details> wrapper.
 * @property {HTMLElement} summaryText - The summary text node updated with selection count.
 *
 * @typedef {object} SelectedAddOn
 * @property {string} variantId - The selected variant id.
 * @property {number} quantity - The quantity to add (always 1 for now).
 * @property {number} price - The unit price in the shop's smallest currency unit.
 *
 * @extends Component<ProductAddOnsRefs>
 */
class ProductAddOnsComponent extends Component {
  requiredRefs = ['summaryText'];

  /** @type {Map<string, SelectedAddOn>} */
  #selected = new Map();

  /**
   * Public API consumed by `product-form.js`.
   * Returns the staged add-ons formatted for `cart/add.js` `items[]` payload.
   *
   * @returns {Array<{ id: number, quantity: number }>}
   */
  getSelectedItems() {
    return Array.from(this.#selected.values()).map(({ variantId, quantity }) => ({
      id: Number(variantId),
      quantity,
    }));
  }

  /**
   * Clears every selection and resets the UI. Called by product-form.js on
   * a successful add-to-cart so the customer doesn't end up with stale state.
   */
  clearSelection() {
    for (const productId of Array.from(this.#selected.keys())) {
      this.#deselect(productId);
    }
    this.#updateSummary();
  }

  /**
   * Toggle the staged state for a given add-on.
   * @param {Event} event
   */
  toggleAddon(event) {
    const button = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('[data-product-id]') : null
    );
    if (!button) return;

    const productId = button.getAttribute('data-product-id');
    if (!productId) return;

    if (this.#selected.has(productId)) {
      this.#deselect(productId);
    } else {
      this.#select(productId);
    }

    this.#updateSummary();
  }

  /**
   * Sync state when a variant <select> changes for an add-on.
   * @param {Event} event
   */
  handleVariantChange(event) {
    const select = /** @type {HTMLSelectElement | null} */ (
      event.target instanceof HTMLSelectElement ? event.target : null
    );
    if (!select) return;

    const productId = select.dataset.productId;
    if (!productId) return;

    const item = this.#getItem(productId);
    if (!item) return;

    this.#updatePriceDisplay(item, select);

    if (this.#selected.has(productId)) {
      const option = select.options[select.selectedIndex];
      if (!option) return;
      this.#selected.set(productId, {
        variantId: option.value,
        quantity: 1,
        price: Number(option.dataset.price) || 0,
      });
      this.#updateSummary();
    }
  }

  /**
   * Optional hook in case we ever want to react to disclosure open/close.
   * Required because the markup declares `on:toggle="/handleToggle"`.
   * @param {Event} _event
   */
  handleToggle(_event) {
    // no-op for now; reserved for future analytics/UX (e.g. focus trap).
  }

  /**
   * @param {string} productId
   */
  #select(productId) {
    const item = this.#getItem(productId);
    if (!item) return;

    const select = /** @type {HTMLSelectElement | null} */ (
      item.querySelector('select.product-add-on__variant-select')
    );
    /** @type {{ variantId: string, price: number }} */
    let payload;
    if (select) {
      const option = select.options[select.selectedIndex];
      if (!option || option.disabled) return;
      payload = {
        variantId: option.value,
        price: Number(option.dataset.price) || 0,
      };
    } else {
      payload = {
        variantId: item.dataset.defaultVariantId || '',
        price: Number(item.dataset.defaultPrice) || 0,
      };
    }

    if (!payload.variantId) return;

    this.#selected.set(productId, { ...payload, quantity: 1 });
    item.setAttribute('data-selected', '');

    const button = /** @type {HTMLButtonElement | null} */ (item.querySelector('.product-add-on__toggle'));
    if (button) {
      button.setAttribute('aria-pressed', 'true');
      this.#updateAriaLabel(button, item, true);
    }
  }

  /**
   * @param {string} productId
   */
  #deselect(productId) {
    this.#selected.delete(productId);
    const item = this.#getItem(productId);
    if (!item) return;

    item.removeAttribute('data-selected');
    const button = /** @type {HTMLButtonElement | null} */ (item.querySelector('.product-add-on__toggle'));
    if (button) {
      button.setAttribute('aria-pressed', 'false');
      this.#updateAriaLabel(button, item, false);
    }
  }

  /**
   * @param {string} productId
   * @returns {HTMLLIElement | null}
   */
  #getItem(productId) {
    return /** @type {HTMLLIElement | null} */ (
      this.querySelector(`.product-add-on[data-product-id="${productId}"]`)
    );
  }

  /**
   * @param {HTMLLIElement} item
   * @param {HTMLSelectElement} select
   */
  #updatePriceDisplay(item, select) {
    const option = select.options[select.selectedIndex];
    if (!option) return;

    const currentPriceEl = item.querySelector('[data-current-price]');
    const comparePriceEl = /** @type {HTMLElement | null} */ (item.querySelector('[data-compare-price]'));

    if (currentPriceEl) {
      currentPriceEl.textContent = option.dataset.priceFormatted || '';
    }
    if (comparePriceEl) {
      const compare = option.dataset.compareFormatted;
      if (compare) {
        comparePriceEl.textContent = compare;
        comparePriceEl.removeAttribute('hidden');
      } else {
        comparePriceEl.textContent = '';
        comparePriceEl.setAttribute('hidden', '');
      }
    }
  }

  /**
   * Updates the summary line: "No Add-Ons Selected" or "X Add-Ons Selected • $YY".
   */
  #updateSummary() {
    const { summaryText } = this.refs;
    const count = this.#selected.size;

    if (count === 0) {
      summaryText.textContent = this.dataset.summaryNone || '';
      return;
    }

    const totalCents = Array.from(this.#selected.values()).reduce((sum, item) => sum + item.price, 0);
    const formattedTotal = this.#formatMoney(totalCents);

    const template = count === 1 ? this.dataset.summaryOne : this.dataset.summaryMany;
    if (!template) {
      summaryText.textContent = `${count} • ${formattedTotal}`;
      return;
    }

    summaryText.textContent = template
      .replace('[count]', String(count))
      .replace('[price]', formattedTotal);
  }

  /**
   * Formats a price (in the shop's smallest currency unit) using the same
   * format pattern that any selected add-on already exposes via its
   * `data-price-formatted` option attribute. Falls back to a simple
   * `{symbol}{amount}` rendering when no template is available yet.
   *
   * @param {number} totalCents
   * @returns {string}
   */
  #formatMoney(totalCents) {
    const sample = /** @type {HTMLElement | null} */ (this.querySelector('option[data-price-formatted]'));
    const formatted = sample?.dataset.priceFormatted;
    const samplePrice = Number(sample?.dataset.price);

    if (formatted && samplePrice > 0) {
      const numericMatch = formatted.match(/[\d.,]+/);
      if (numericMatch) {
        const numericString = numericMatch[0];
        const decimalSeparator = numericString.includes(',') && numericString.lastIndexOf(',') > numericString.lastIndexOf('.')
          ? ','
          : '.';
        const decimals = numericString.split(decimalSeparator)[1]?.length ?? 2;
        const totalUnits = totalCents / 100;
        const replacement = totalUnits
          .toFixed(decimals)
          .replace('.', decimalSeparator);

        return formatted.replace(numericString, replacement);
      }
    }

    return (totalCents / 100).toFixed(2);
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {HTMLLIElement} item
   * @param {boolean} isSelected
   */
  #updateAriaLabel(button, item, isSelected) {
    const title = item.dataset.productTitle || '';
    const template = isSelected ? this.dataset.ariaRemoveTemplate : this.dataset.ariaAddTemplate;
    if (template) {
      button.setAttribute('aria-label', template.replace('[title]', title));
    }
  }
}

if (!customElements.get('product-add-ons-component')) {
  customElements.define('product-add-ons-component', ProductAddOnsComponent);
}

export { ProductAddOnsComponent };
